import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { placesRepository } from './api/places-repository'
import { SURFACE_LABELS, cityKeyForSpain, type PadelPlace } from '@/core/models/padel-place'
import type { PricingRuleInput } from '@upadelmatch/core'
import type { GridCourt } from './domain/slot-grid'
import { findClub, mockClubs, mockBookingFactsFor } from '@/shared/mock/data'
import { usePlayerLocation } from './use-player-location'

// The view shape every lugares surface renders (card, detail, picker). Mirrors
// the certified stub's MockClub, with two honesty upgrades for real data:
// distance and WhatsApp are null when genuinely unknown — the UI omits them
// instead of showing a fabricated number/link.
export interface DirectoryClub {
  id: string
  name: string
  city: string
  address: string
  courts: number
  surface: string
  indoor: boolean
  distanceKm: number | null
  phone: string | null
  whatsapp: string | null
  amenities: string[]
  /**
   * GOOGLE's place id (not ours), stored by scripts/geocode_places.py. Drives
   * `destination_place_id` on "Cómo llegar" so the route targets the venue's
   * own Google listing rather than our coordinates — which matters because 10
   * of the 48 places geocoded only to their TOWN centroid. Null for any place
   * that predates the backfill.
   */
  googlePlaceId: string | null
  /**
   * Coordinates for plotting, null until a place is geocoded. Kept separate
   * from `distanceKm` on purpose: distance depends on an ORIGIN that may not
   * exist (no centroid, no player fix), while a pin only needs the place
   * itself. Conflating them is why the map and the km can disagree.
   */
  lat: number | null
  lng: number | null
  /**
   * The SLOW half of the slot grid, or null for a club that cannot be booked
   * through the app — which is most of them (38 of the first 48 production
   * rows are places we listed ourselves that never connected to anything).
   *
   * Null is a first-class answer, not a missing value: every consumer reads it
   * as "no availability section, contact the club" and the screen is complete
   * without it. Assembled here, once, so no screen has to know which
   * combination of projected fields adds up to "bookable".
   */
  booking: ClubBookingFacts | null
}

/** Everything the player's slot grid needs that does NOT change minute to
 *  minute. Rides the cached directory row; the occupied-set comes live from
 *  `getClubAvailability`. */
export interface ClubBookingFacts {
  /** The club's id in the OTHER project — the key `getClubAvailability` takes. */
  clubManagerId: string
  /** IANA zone of the club. Every slot and dateKey anchors here. */
  timeZone: string
  /** Raw `{periods, specialDays}` map; core's resolver reads it. */
  openingHours: unknown
  slotDurationMinutes: number
  courts: GridCourt[]
  clubDefaults: PricingRuleInput[]
  /** Days visible in the grid, today included (club-configurable, default 2). */
  advanceBookingDays: number
}

/**
 * The projection ships pricing RULES rather than prices, so the shared
 * resolver runs client-side — a price computed upstream goes stale the moment
 * the club edits a rule. They arrive as plain maps; this narrows them to the
 * resolver's shape and DROPS anything malformed rather than coercing it.
 *
 * Coercing is the dangerous option: a rule with a NaN price resolves to a NaN
 * amount, which renders as "NaN €" or, worse, as nothing at all next to a slot
 * the player then books expecting it to be free.
 */
function toPricingRules(raw: readonly Record<string, unknown>[]): PricingRuleInput[] {
  const out: PricingRuleInput[] = []
  for (const r of raw) {
    const days = r['daysOfWeek']
    const price = r['price']
    const mode = r['pricingMode']
    if (
      !Array.isArray(days) ||
      typeof r['startTime'] !== 'string' ||
      typeof r['endTime'] !== 'string' ||
      typeof price !== 'number' ||
      !Number.isFinite(price) ||
      (mode !== 'perHour' && mode !== 'perSlot')
    ) continue
    out.push({
      daysOfWeek: days.filter((d): d is number => typeof d === 'number'),
      startTime: r['startTime'],
      endTime: r['endTime'],
      price,
      pricingMode: mode,
      priority: typeof r['priority'] === 'number' ? r['priority'] : 0,
      startDate: typeof r['startDate'] === 'string' ? r['startDate'] : null,
      endDate: typeof r['endDate'] === 'string' ? r['endDate'] : null,
    })
  }
  return out
}

/**
 * Every condition that must hold for this club to show a slot grid, in one
 * place. Any one of them missing ⇒ null ⇒ the contact CTA carries the screen.
 *
 * `bookable !== true` is checked FIRST and strictly: a club that switched
 * «Tu club en la app» off has these fields actively cleared, but a row written
 * before the flag existed has `bookable` absent — and absent must never read as
 * yes for a screen that offers a stranger a court.
 */
function bookingFactsFor(place: PadelPlace): ClubBookingFacts | null {
  if (place.bookable !== true) return null
  if (!place.clubManagerId || !place.timezone) return null
  if (!place.openingHours) return null
  const courts = place.bookableCourts
    .filter((c) => c.id !== '')
    .map((c) => ({ id: c.id, name: c.name, pricingRules: toPricingRules(c.pricingRules) }))
  // No bookable court is not an empty grid, it is no grid: the club has the
  // module on but nothing to offer (all courts in maintenance, say).
  if (courts.length === 0) return null
  return {
    clubManagerId: place.clubManagerId,
    timeZone: place.timezone,
    openingHours: place.openingHours,
    slotDurationMinutes:
      place.slotDurationMinutes && place.slotDurationMinutes > 0 ? place.slotDurationMinutes : 90,
    courts,
    clubDefaults: [],
    advanceBookingDays:
      place.advanceBookingDays && place.advanceBookingDays > 0 ? place.advanceBookingDays : 2,
  }
}

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371
  const dLat = ((bLat - aLat) * Math.PI) / 180
  const dLng = ((bLng - aLng) * Math.PI) / 180
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(s))
}

/**
 * Distances are measured from the centroid of the queried city's pinned
 * places — a stable "city centre" that needs no geolocation permission. With
 * fewer than 2 pinned places there is no meaningful centre, so distances stay
 * null and the UI omits them (never "0.0 km" on a one-club city).
 */
export function directoryOrigin(places: PadelPlace[]): { lat: number; lng: number } | null {
  const pinned = places.filter((p) => p.location?.latitude != null && p.location?.longitude != null)
  if (pinned.length < 2) return null
  return {
    // location/latitude/longitude are non-null BY CONSTRUCTION: `pinned` is
    // filtered on exactly those being present, two lines above.
    lat: pinned.reduce((s, p) => s + p.location!.latitude!, 0) / pinned.length,
    lng: pinned.reduce((s, p) => s + p.location!.longitude!, 0) / pinned.length,
  }
}

/** Pure projection → view mapping (exported for tests). */
export function placeToDirectoryClub(
  place: PadelPlace,
  origin: { lat: number; lng: number } | null,
): DirectoryClub {
  const lat = place.location?.latitude
  const lng = place.location?.longitude
  const distanceKm =
    origin && lat != null && lng != null
      ? Math.round(haversineKm(origin.lat, origin.lng, lat, lng) * 10) / 10
      : null
  return {
    id: place.id,
    name: place.name,
    city: place.city,
    address: place.address,
    courts: place.numberOfCourts ?? 0,
    surface: (place.surfaceType && SURFACE_LABELS[place.surfaceType]) || place.surfaceType || '—',
    indoor: place.isIndoor === true,
    distanceKm,
    phone: place.phoneNumber ?? null,
    whatsapp: place.whatsappNumber ?? null,
    amenities: place.amenities,
    googlePlaceId: place.location?.placeId ?? null,
    lat: place.location?.latitude ?? null,
    lng: place.location?.longitude ?? null,
    booking: bookingFactsFor(place),
  }
}

/**
 * Mock fixture → the view shape, for the dev bypass.
 *
 * EXPORTED because the place picker and the lugares list each had their own
 * inline copy of this mapping. Three copies meant three places to remember
 * when the view shape grew a field — and when it grew `booking`, all three
 * broke at once. One mapping, one place to update.
 */
export function mockToDirectoryClub(m: (typeof mockClubs)[number]): DirectoryClub {
  // The design fixtures carry no Google place id, and inventing one would make
  // the bypass walk exercise a code path production cannot reach. Null is the
  // honest value; "Cómo llegar" falls back to the text query, exactly as it
  // does for a real place that predates the geocoding backfill.
  // Fixtures carry no real coordinates, so the map falls back to the designed
  // surface under the bypass rather than plotting invented pins.
  //
  // `booking` is fixtured for SOME clubs on purpose. A bypass that returns null
  // for every club renders no availability section at all, so the whole slice
  // would be invisible to anyone walking the app — the failure recorded in
  // `feedback_bypass_hides_counter_driven_ui`, where mocked-to-zero counters
  // hid a screen that was working. c1 is bookable, c3 is not, so both branches
  // are reachable by eye.
  return { ...m, googlePlaceId: null, lat: null, lng: null, booking: mockBookingFactsFor(m.id) }
}

/**
 * Rounded to ~110 m for the query key only. A raw GPS fix changes on every
 * read, so keying on it would mint a fresh cache entry per jitter and refetch
 * the directory continuously. The distance maths still uses the exact fix.
 */
function originKey(p: { lat: number; lng: number } | null): string {
  return p ? `${p.lat.toFixed(3)},${p.lng.toFixed(3)}` : 'centroid'
}

/** Active directory for a city (real auth) — one read per city per session. */
export function useDirectoryPlaces(city: string) {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  // The player's own position when they have offered it; otherwise null and the
  // city centroid stands in, exactly as before. Never prompts — see
  // use-player-location.ts.
  const player = usePlayerLocation((s) => s.position)
  return useQuery({
    queryKey: ['places', cityKeyForSpain(city), originKey(player)],
    queryFn: async () => {
      const places = await placesRepository.listActiveByCityKey(cityKeyForSpain(city))
      const origin = player ?? directoryOrigin(places)
      return places
        .map((p) => placeToDirectoryClub(p, origin))
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
    },
    enabled: !AUTH_BYPASS && uid !== null,
    staleTime: 5 * 60_000,
  })
}

/** One place by id — detail page + picker prefill. Mock under the bypass. */
export function usePlace(id: string | undefined) {
  // A single place has no city list to average, so the centroid is unavailable
  // here and distance was ALWAYS null on this path. The player's own fix is the
  // one origin that works for one club — so club detail can finally show a
  // distance, and only when it is a real one.
  const player = usePlayerLocation((s) => s.position)
  const query = useQuery({
    queryKey: ['place', id, originKey(player)],
    queryFn: async () => {
      // `id!` — the query is `enabled` only when id is defined.
      const place = await placesRepository.getById(id!)
      return place ? placeToDirectoryClub(place, player) : null
    },
    // No `uid` gate: reading ONE place by id is `allow get: if true` in the
    // rules, deliberately, so /unete/:id works for a signed-out prospect —
    // which is the entire point of a club invite link. Enumeration is still
    // `list: if signedIn()`, and `useAllPlaces`/`useDirectoryPlaces` below
    // keep their session gate because they enumerate.
    enabled: !AUTH_BYPASS && !!id,
    staleTime: 5 * 60_000,
  })
  if (AUTH_BYPASS) {
    const mock = findClub(id)
    return { club: mock ? mockToDirectoryClub(mock) : null, isPending: false, isError: false }
  }
  return { club: query.data ?? null, isPending: query.isPending, isError: query.isError }
}

/** Whole active directory — the place picker's haystack. */
export function useAllPlaces() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['places', 'all'],
    queryFn: async () => {
      const places = await placesRepository.listActive()
      return places
        .map((p) => placeToDirectoryClub(p, null))
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
    },
    enabled: !AUTH_BYPASS && uid !== null,
    staleTime: 5 * 60_000,
  })
}
